import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import grillImageMini from '../../assets/images/Minis.png';
import grillImageSmall from '../../assets/images/small.png';
import grillImageMedium from '../../assets/images/medium.png';
import retainingWallImage from '../../assets/images/estwall.png';
import concretePatioImage from '../../assets/images/pad.png';
import pergolaImage from '../../assets/images/pergola.png';
import CalculatorResults from './CalculatorResults';

const CalculatorContainer = styled(motion.div)`
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02));
  backdrop-filter: blur(10px);
  border-radius: 32px;
  padding: 3rem;
  max-width: 1200px;
  margin: 2rem auto;
  box-shadow: 0 25px 50px -12px rgba(255, 87, 34, 0.15);
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 87, 34, 0.1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(255, 87, 34, 0.5), 
      transparent
    );
  }

  @media (max-width: 768px) {
    padding: 2rem;
    margin: 1rem;
  }
`;

const Title = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 2.5rem);
  background: linear-gradient(135deg, #FF5722 0%, #FF7043 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
  margin-bottom: 2rem;
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const Description = styled.p`
  font-size: 1.1rem;
  color: var(--text-light);
  text-align: center;
  margin-bottom: 2.5rem;
  line-height: 1.6;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const Select = styled(motion.select)`
  width: 100%;
  padding: 1.2rem;
  border-radius: 16px;
  border: 2px solid rgba(255, 87, 34, 0.2);
  background: rgba(255, 255, 255, 0.05);
  color: #FF5722;
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
  cursor: pointer;
  backdrop-filter: blur(10px);

  &:hover {
    border-color: #FF5722;
    box-shadow: 0 8px 30px rgba(255, 87, 34, 0.15);
    transform: translateY(-2px);
  }

  &:focus {
    outline: none;
    border-color: #FF7043;
    box-shadow: 0 8px 30px rgba(255, 87, 34, 0.2);
  }

  option {
    background: white;
    color: #FF5722;
    padding: 1rem;
  }
`;

const Input = styled(motion.input)`
  width: 100%;
  padding: 1.2rem;
  border-radius: 16px;
  border: 2px solid rgba(255, 87, 34, 0.2);
  background: rgba(255, 255, 255, 0.05);
  color: #FF5722;
  font-size: 1.1rem;
  margin-bottom: 1rem;
  transition: all 0.3s ease;

  &:hover {
    border-color: #FF5722;
    transform: translateY(-2px);
  }

  &:focus {
    outline: none;
    border-color: #FF7043;
    box-shadow: 0 8px 30px rgba(255, 87, 34, 0.2);
  }

  &::placeholder {
    color: rgba(255, 87, 34, 0.5);
  }
`;

const StyledButton = styled(motion.button)`
  width: 100%;
  padding: 1.2rem;
  border-radius: 16px;
  border: none;
  background: linear-gradient(135deg, #FF5722 0%, #FF7043 100%);
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 2rem;
  box-shadow: 0 8px 30px rgba(255, 87, 34, 0.2);

  &:disabled {
    background: linear-gradient(135deg, #ccc 0%, #ddd 100%);
    cursor: not-allowed;
    box-shadow: none;
  }

  &:not(:disabled):hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(255, 87, 34, 0.3);
  }
`;

const ResultContainer = styled(motion.div)`
  margin-top: 2rem;
  padding: 2rem;
  border-radius: 24px;
  background: var(--background-light);
  border: 1px solid rgba(255, 107, 0, 0.1);
`;

const ProjectImage = styled.img`
  width: 100%;
  max-width: 600px;
  height: auto;
  border-radius: 20px;
  margin: 2rem auto;
  display: block;
  box-shadow: 0 20px 40px rgba(255, 107, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const ProjectDetails = styled.div`
  font-size: 1.1rem;
  color: var(--text-light);
  line-height: 1.6;
  text-align: center;
  max-width: 800px;
  margin: 1.5rem auto;
  padding: 1.5rem;
  background: rgba(44, 44, 44, 0.8);
  border-radius: 16px;
  border: 1px solid rgba(255, 107, 0, 0.1);
`;

const ContactSection = styled(motion.div)`
  text-align: center;
  padding: 2rem;
  background: rgba(44, 44, 44, 0.9);
  border-radius: 20px;
  border: 1px solid rgba(255, 107, 0, 0.1);
`;

const ContactHeading = styled.h4`
  color: var(--text);
  font-size: 1.3rem;
  margin-bottom: 1rem;
`;

const ContactText = styled.p`
  color: var(--text-light);
  margin-bottom: 1.5rem;
`;

const LeadCaptureButton = styled(StyledButton)`
  max-width: 300px;
  margin: 1.5rem auto 0;
  display: block;
`;

const ProjectTypeDescription = styled.div`
  color: var(--text-light);
  font-size: 0.9rem;
  line-height: 1.5;
  padding: 1rem;
  margin: -1rem 0 1rem;
  background: rgba(255, 107, 0, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 107, 0, 0.1);
`;

const ErrorMessage = styled.div`
    color: red;
    padding: 1rem;
    margin: 1rem 0;
    background: rgba(255, 0, 0, 0.1);
    border-radius: 8px;
    text-align: center;
`;

const ResultsContainer = styled.div`
    margin-top: 2rem;
    padding: 2rem;
    border-radius: 24px;
    background: var(--background-light);
    border: 1px solid rgba(255, 107, 0, 0.1);
`;

const EstimateAmount = styled.div`
    font-size: 2.5rem;
    font-weight: 700;
    color: #FF5722;
    text-align: center;
    margin-bottom: 1.5rem;
`;

const SelectionsSummary = styled.div`
    background: rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 1.5rem;
    margin: 1.5rem 0;
    border: 1px solid rgba(255, 255, 255, 0.1);
`;

const SummaryTitle = styled.h3`
    color: #FF5722;
    font-size: 1.2rem;
    margin-bottom: 1rem;
    text-align: center;
`;

const SelectionItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    &:last-child {
        border-bottom: none;
    }
`;

const SelectionLabel = styled.span`
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
`;

const SelectionValue = styled.span`
    color: white;
    font-weight: 500;
`;

const ActionButtons = styled.div`
    display: flex;
    gap: 1rem;
    justify-content: center;
    margin-top: 2rem;
`;

const PrimaryButton = styled(motion.button)`
    padding: 1rem 2rem;
    background: #FF5722;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 1.1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background: #F4511E;
        transform: translateY(-2px);
    }
`;

const SecondaryButton = styled(PrimaryButton)`
    background: transparent;
    border: 2px solid #FF5722;
    color: #FF5722;

    &:hover {
        background: rgba(255, 87, 34, 0.1);
    }
`;

const Calculator = () => {
  const [projectType, setProjectType] = useState('outdoor-kitchen');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [result, setResult] = useState(null);
  const [calculationSteps, setCalculationSteps] = useState([]);
  const [totalSquareFootage, setTotalSquareFootage] = useState(null);
  const [projectImage, setProjectImage] = useState(null);
  const [projectDetails, setProjectDetails] = useState('');
  const [estimate, setEstimate] = useState(null);
  const [additionalOptions, setAdditionalOptions] = useState({
    premium: false,
    rush: false,
    warranty: false
  });
  const [showOptions, setShowOptions] = useState(false);
  const [error, setError] = useState('');
  const [selectedOptions, setSelectedOptions] = useState([]);

  const navigate = useNavigate();

  const isFormValid = () => {
    switch (projectType) {
      case 'outdoor-kitchen':
        return length >= 6 && zipCode.length === 5;
      case 'retaining-wall':
        return length > 0 && height > 0 && zipCode.length === 5;
      case 'concrete-patio':
        return length > 0 && height > 0 && zipCode.length === 5;
      case 'pergola':
        return length > 0 && width > 0 && zipCode.length === 5;
      default:
        return false;
    }
  };

  const handleCalculate = () => {
    let total = 0;
    const steps = [];
    let squareFootageCalc = 0;

    switch (projectType) {
      case 'outdoor-kitchen':
        const depth = 32 / 12;
        total = parseFloat(length) * 825;
        steps.push(`Length: ${length} feet * Rate: $825 per linear foot = $${total.toFixed(2)}`);
        squareFootageCalc = parseFloat(length) * depth;
        
        if (length >= 6 && length <= 10) {
          setProjectImage(grillImageMini);
          setProjectDetails('A mini outdoor kitchen (less than 10 linear feet) is the simplest, typically including a grill, some storage, and a small countertop.');
        } else if (length >= 11 && length <= 15) {
          setProjectImage(grillImageSmall);
          setProjectDetails('A small, basic outdoor kitchen (about 13 linear feet) has room for a few more upgrades, like an added cooktop, a mini fridge, and a sink.');
        } else if (length >= 16) {
          setProjectImage(grillImageMedium);
          setProjectDetails('A medium-sized kitchen (about 16 linear feet) can include all of the above, plus extras, such as more storage, countertop space, and maybe even a small seating area.');
        } else {
          setProjectImage(grillImageMini); // Default to the smallest option
          setProjectDetails('Please enter a length of at least 6 feet for a valid outdoor kitchen.');
        }
        break;
      case 'retaining-wall':
        const initialHeight = parseFloat(height);
        const adjustedHeight = initialHeight + 1; // Add 1 foot to the height
        total = parseFloat(length) * adjustedHeight * 25;
        steps.push(`Length: ${length} feet * Height: ${adjustedHeight} feet (including 1 extra foot) * Rate: $25 per square foot = $${total.toFixed(2)}`);
        squareFootageCalc = parseFloat(length) * adjustedHeight;
    
        if (initialHeight >= 4) {
            steps.push("Note: Since the selected height is 4 feet or more, an engineered wall and permits from the city are typically required in most areas.");
        }
    
        setProjectImage(retainingWallImage);
        setProjectDetails('Retaining wall estimate includes materials, labor, and basic drainage. Engineering costs not included.');
        break;
      case 'concrete-patio':
        total = parseFloat(length) * parseFloat(height) * 10;
        steps.push(`Length: ${length} feet * Width: ${height} feet * Rate: $10 per square foot = $${total.toFixed(2)}`);
        squareFootageCalc = parseFloat(length) * parseFloat(height);
        setProjectImage(concretePatioImage);
        break;
      case 'pergola':
        total = parseFloat(length) * parseFloat(width) * 40;
        steps.push(`Length: ${length} feet * Width: ${width} feet * Rate: $40 per square foot = $${total.toFixed(2)}`);
        squareFootageCalc = parseFloat(length) * parseFloat(width);
        setProjectImage(pergolaImage);
        break;
      default:
        total = 0;
        setProjectImage(null);
        setProjectDetails('');
    }

    setResult(total);
    setCalculationSteps(steps);
    setTotalSquareFootage(squareFootageCalc);

    const timestamp = new Date().toISOString();
    const data = {
      projectType,
      length,
      width,
      height,
      zipCode,
      result: total,
      totalSquareFootage: squareFootageCalc,
      timestamp,
    };
    handleSubmit(data);
    saveResultToLocalStorage(data);

    const estimateData = {
      amount: Math.round(total),
      details: {
        service: projectType,
        dimensions: getDimensionsString(),
        area: `${squareFootageCalc.toFixed(2)} sq ft`,
        options: getSelectedOptions(),
        zipCode: zipCode,
        projectImage: projectImage,
        projectDetails: projectDetails,
        calculationSteps: calculationSteps
      }
    };

    // Store in localStorage
    localStorage.setItem('pendingEstimate', JSON.stringify(estimateData));
    
    // Set the estimate state
    setEstimate(estimateData);
  };

  const handleSubmit = async (data) => {
    try {
        const result = calculateEstimate(data);
        
        // Create selections summary
        const selections = {
            projectType: data.projectType.split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' '),
            dimensions: `${data.length}' x ${data.width}'`,
            area: `${result.squareFootageCalc.toFixed(2)} sq ft`,
            location: data.zipCode
        };

        // Prepare data for backend
        const estimateData = {
            userId: localStorage.getItem('userId'),
            amount: result.total,
            projectDetails: {
                service: data.projectType,
                length: data.length,
                width: data.width,
                height: data.height,
                area: `${result.squareFootageCalc.toFixed(2)} sq ft`,
                zipCode: data.zipCode,
                totalSquareFootage: result.squareFootageCalc
            },
            selections
        };

        // Save to backend if user is logged in
        const token = localStorage.getItem('authToken');
        if (token) {
            const response = await fetch('http://localhost:5001/api/estimates', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(estimateData)
            });

            if (!response.ok) {
                throw new Error('Failed to save estimate');
            }
        }

        // Update local state
        setSelectedOptions(Object.entries(selections).map(([label, value]) => ({
            label,
            value
        })));
        setEstimate(result.total);
        setShowOptions(true);

    } catch (error) {
        console.error('Error calculating estimate:', error);
        setError('Failed to calculate estimate');
    }
  };

  const saveResultToLocalStorage = (data) => {
    localStorage.setItem('projectData', JSON.stringify(data));
  };

  const renderInputs = () => {
    switch (projectType) {
      case 'outdoor-kitchen':
        return (
          <input
            type="number"
            value={length}
            onChange={(e) => setLength(e.target.value)}
            placeholder="Enter length in feet (minimum of 6ft)"
            className="input-field"
          />
        );
      case 'retaining-wall':
        return (
          <>
            <input
              type="number"
              value={length}
              onChange={(e) => setLength(e.target.value)}
              placeholder="Enter length in feet"
              className="input-field"
            />
            <input
              type="number"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              placeholder="Enter height in feet"
              className="input-field"
            />
          </>
        );
      case 'concrete-patio':
        return (
          <>
            <input
              type="number"
              value={length}
              onChange={(e) => setLength(e.target.value)}
              placeholder="Enter length in feet"
              className="input-field"
            />
            <input
              type="number"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              placeholder="Enter width in feet"
              className="input-field"
            />
          </>
        );
      case 'pergola':
        return (
          <>
            <input
              type="number"
              value={length}
              onChange={(e) => setLength(e.target.value)}
              placeholder="Enter length in feet"
              className="input-field"
            />
            <input
              type="number"
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              placeholder="Enter width in feet"
              className="input-field"
            />
          </>
        );
      default:
        return null;
    }
  };

  const getProjectDescription = (type) => {
    switch (type) {
      case 'outdoor-kitchen':
        return 'Design your dream outdoor kitchen. Includes countertops, appliance space, and storage. Minimum length of 6 feet required.';
      case 'retaining-wall':
        return 'Calculate costs for retaining walls. Price includes materials, labor, and basic drainage. Engineering may be required for walls over 4 feet.';
      case 'concrete-patio':
        return 'Estimate your concrete patio project. Includes site prep, forms, concrete, and basic finish. Decorative finishes may cost extra.';
      case 'pergola':
        return 'Design your pergola. Price includes lumber, hardware, concrete footings, and labor. Custom designs available.';
      default:
        return '';
    }
  };

  const getDimensionsString = () => {
    let dimensions = '';
    if (length) dimensions += `Length: ${length}'`;
    if (width) dimensions += ` Width: ${width}'`;
    if (height) dimensions += ` Height: ${height}'`;
    return dimensions;
  };

  const getSelectedOptions = () => {
    return Object.entries(additionalOptions)
      .filter(([, value]) => value)
      .map(([key]) => key.charAt(0).toUpperCase() + key.slice(1));
  };

  const handleOptionChange = (option) => {
    setAdditionalOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const calculateEstimate = (data) => {
    try {
      const { length, width, projectType } = data;
      const area = length * width;
      
      let baseRate;
      switch (projectType) {
        case 'roofing':
          baseRate = 7.50;
          break;
        case 'outdoor-kitchen':
          baseRate = 150;
          break;
        case 'retaining-wall':
          baseRate = 35;
          break;
        default:
          baseRate = 10;
      }

      const estimate = area * baseRate;
      return {
        total: estimate,
        squareFootageCalc: area
      };
    } catch (err) {
      setError('Error calculating estimate');
      return {
        total: 0,
        squareFootageCalc: 0
      };
    }
  };

  // Show CalculatorResults if we have an estimate
  if (estimate) {
    return (
      <CalculatorResults 
        estimate={estimate.amount} 
        projectDetails={{
          ...estimate.details,
          projectImage: projectImage,
          projectDescription: projectDetails
        }}
      />
    );
  }

  return (
    <CalculatorContainer
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <Title variants={itemVariants}>
        Project Cost Calculator
      </Title>
      <Description>
        Get instant estimates for your outdoor projects. Choose a project type and enter dimensions 
        for a quick cost projection.
      </Description>
      
      <FormSection variants={itemVariants}>
        <FormGroup>
          <Label>Project Type</Label>
          <Select 
            value={projectType} 
            onChange={(e) => setProjectType(e.target.value)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <option value="outdoor-kitchen">Outdoor Kitchen</option>
            <option value="retaining-wall">Retaining Wall</option>
            <option value="concrete-patio">Concrete Patio</option>
            <option value="pergola">Pergola</option>
          </Select>
          <ProjectTypeDescription>
            {getProjectDescription(projectType)}
          </ProjectTypeDescription>
        </FormGroup>

        <InputGrid>
          {renderInputs()}
        </InputGrid>

        <FormGroup>
          <Label>Location</Label>
          <Input
            type="text"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            placeholder="Enter your zip code"
            pattern="[0-9]*"
            maxLength="5"
          />
        </FormGroup>

        <OptionsSection>
          <OptionItem>
            <Checkbox
              type="checkbox"
              checked={additionalOptions.premium}
              onChange={() => handleOptionChange('premium')}
              id="premium"
            />
            <OptionLabel htmlFor="premium">Premium Materials (+20%)</OptionLabel>
          </OptionItem>
          <OptionItem>
            <Checkbox
              type="checkbox"
              checked={additionalOptions.rush}
              onChange={() => handleOptionChange('rush')}
              id="rush"
            />
            <OptionLabel htmlFor="rush">Rush Service (+15%)</OptionLabel>
          </OptionItem>
          <OptionItem>
            <Checkbox
              type="checkbox"
              checked={additionalOptions.warranty}
              onChange={() => handleOptionChange('warranty')}
              id="warranty"
            />
            <OptionLabel htmlFor="warranty">Extended Warranty (+10%)</OptionLabel>
          </OptionItem>
        </OptionsSection>

        <StyledButton
          onClick={handleCalculate}
          disabled={!isFormValid()}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Calculate Estimate
        </StyledButton>
      </FormSection>

      {result !== null && (
        <ResultContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <ResultHeader>
            <ResultIcon>📊</ResultIcon>
            <ResultTitle>Project Estimate</ResultTitle>
          </ResultHeader>

          <ResultGrid>
            <ResultCard>
              <ResultLabel>Square Footage</ResultLabel>
              <ResultValue>{totalSquareFootage.toFixed(2)} sq ft</ResultValue>
            </ResultCard>

            <ResultCard>
              <ResultLabel>Base Cost</ResultLabel>
              <ResultValue>${result.toFixed(2)}</ResultValue>
            </ResultCard>
          </ResultGrid>

          <CalculationSteps>
            {calculationSteps.map((step, index) => (
              <StepItem 
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <StepNumber>{index + 1}</StepNumber>
                <StepText>{step}</StepText>
              </StepItem>
            ))}
          </CalculationSteps>

          {projectImage && (
            <ImageSection
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              <ProjectImage src={projectImage} alt="Project Preview" />
              {projectDetails && (
                <ProjectDetails>{projectDetails}</ProjectDetails>
              )}
            </ImageSection>
          )}

          <ContactSection
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <ContactHeading>Ready to Get Started?</ContactHeading>
            <ContactText>
              Get a detailed quote customized to your specific needs and preferences.
            </ContactText>
            <LeadCaptureButton
              onClick={() => navigate('/contact')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Get Detailed Quote
            </LeadCaptureButton>
          </ContactSection>
        </ResultContainer>
      )}

      {showOptions && estimate && (
        <ResultsContainer>
          <EstimateAmount>${estimate.toLocaleString()}</EstimateAmount>
          
          <SelectionsSummary>
            <SummaryTitle>Project Details</SummaryTitle>
            {selectedOptions.map((option, index) => (
              <SelectionItem key={index}>
                <SelectionLabel>{option.label}:</SelectionLabel>
                <SelectionValue>{option.value}</SelectionValue>
              </SelectionItem>
            ))}
          </SelectionsSummary>

          <ActionButtons>
            <PrimaryButton onClick={() => navigate('/signup')}>
              Save & Create Account
            </PrimaryButton>
            <SecondaryButton onClick={() => navigate('/contact')}>
              Contact Us
            </SecondaryButton>
          </ActionButtons>
        </ResultsContainer>
      )}

      {error && (
        <ErrorMessage>
          {error}
        </ErrorMessage>
      )}
    </CalculatorContainer>
  );
};

// New styled components for improved UX
const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: var(--text);
  font-size: 0.9rem;
  font-weight: 500;
  margin-left: 0.5rem;
`;

const InputGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
`;

const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const ResultIcon = styled.div`
  font-size: 2rem;
`;

const ResultTitle = styled.h3`
  font-size: 1.5rem;
  color: var(--text);
  margin: 0;
`;

const ResultGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const ResultCard = styled(motion.div)`
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
  padding: 2rem;
  border-radius: 20px;
  border: 2px solid rgba(255, 87, 34, 0.1);
  box-shadow: 0 15px 40px rgba(255, 87, 34, 0.1);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 50px rgba(255, 87, 34, 0.15);
  }
`;

const ResultLabel = styled.div`
  color: var(--text-light);
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

const ResultValue = styled.div`
  color: var(--text);
  font-size: 1.5rem;
  font-weight: 600;
`;

const CalculationSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 2rem 0;
`;

const StepItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(44, 44, 44, 0.9);
  border-radius: 12px;
  border: 1px solid rgba(255, 107, 0, 0.1);
`;

const StepNumber = styled.div`
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  color: white;
  flex-shrink: 0;
`;

const StepText = styled.div`
  color: var(--text-light);
  font-size: 0.95rem;
  line-height: 1.5;
`;

const ImageSection = styled(motion.div)`
  margin: 3rem 0;
`;

const OptionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 1.5rem 0;
`;

const OptionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const OptionLabel = styled.label`
  color: var(--text);
  font-size: 0.9rem;
  cursor: pointer;
`;

// Add animation variants
const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6,
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

export default Calculator;
